import React, { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import './AdminContent.css';

const formatCredits = (value) => `${value ?? 0} credit${Number(value) === 1 ? '' : 's'}`;
const formatPlayStyleLabel = (value) => {
  if (value === 'solo') return 'Solo';
  if (value === 'group_solo') return 'Group + Solo';
  return 'Group';
};
const formatAvailableEnvironments = (value) => (
  (value?.length ? value : DEFAULT_CONTENT_ENVIRONMENTS)
    .map((environment) => CONTENT_ENVIRONMENT_OPTIONS.find((option) => option.value === environment)?.label || environment)
    .join(', ')
);
const DEFAULT_IDLE_RECOVERY_PROMPT = 'The game has been inactive for several seconds. Continue from the current game state. If you are waiting for player participation, briefly re-engage them in character. If a player answer or tool result is already available, proceed to the next required step. Keep it short, do not reveal hidden information, and do not skip required game flow.';
const CONTENT_ENVIRONMENT_OPTIONS = [
  { value: 'development', label: 'Development' },
  { value: 'staging', label: 'Staging' },
  { value: 'production', label: 'Production' },
];
const DEFAULT_CONTENT_ENVIRONMENTS = CONTENT_ENVIRONMENT_OPTIONS.map((option) => option.value);
const GAME_MODE_TOOL_OPTIONS = [
  'update_scores',
  'display_question',
  'end_game',
  'getExistingHosts',
  'saveHost',
  'updateHost',
  'getStudioAssets',
  'saveStudioAsset',
  'updateStudioAsset',
  'getExistingQuestions',
  'getExistingGameModes',
  'getExistingQuestPacks',
  'saveQuestPack',
  'updateQuestPack',
  'saveGameMode',
  'updateGameMode',
  'getExistingGamePacks',
  'saveGamePack',
  'updateGamePack',
  'saveQuestion',
  'updateQuestion',
];

const tabs = [
  { to: '/admin/content/game-packs', label: 'Game Packs', key: 'game-packs' },
  { to: '/admin/content/game-modes', label: 'Game Modes', key: 'game-modes' },
  { to: '/admin/content/quest-packs', label: 'Quest Packs', key: 'quest-packs' },
  { to: '/admin/content/questions', label: 'Questions', key: 'questions' },
  { to: '/admin/content/hosts', label: 'Hosts', key: 'hosts' },
];

const configs = {
  'game-packs': {
    title: 'Game Packs',
    endpoint: '/.netlify/functions/admin-get-game-packs',
    responseKey: 'game_packs',
    filters: ['query', 'active'],
    listLabel: (item) => item.name,
    meta: (item) => `${item.game_mode?.name || 'No Game Mode'} • ${formatCredits(item.session_credit_cost)}`,
    detail: (item) => [
      ['Status', item.status],
      ['Game Mode', item.game_mode?.name || 'Unknown'],
      ['Host', item.associated_host?.name || 'Unknown'],
      ['Cost to Play', formatCredits(item.session_credit_cost)],
      ['Available In', formatAvailableEnvironments(item.available_environments)],
      ['Game Image', item.image_filename || 'Using host image fallback'],
      ['Sessions Played', item.sessions_played],
      ['Quest Packs', item.associated_quest_packs?.map((pack) => pack.name).join(', ') || 'None'],
      ['Created', item.created_at],
      ['Updated', item.updated_at],
    ],
  },
  'game-modes': {
    title: 'Game Modes',
    endpoint: '/.netlify/functions/admin-get-game-modes',
    responseKey: 'game_modes',
    filters: ['query', 'tag', 'include_questions'],
    listLabel: (item) => item.name,
    meta: (item) => `${formatPlayStyleLabel(item.play_style)} • ${item.tool_names?.length || 0} tools`,
    detail: (item) => [
      ['Description', item.description || 'No description'],
      ['Play Style', formatPlayStyleLabel(item.play_style)],
      ['Available In', formatAvailableEnvironments(item.available_environments)],
      ['Realtime Model', item.realtime_model || 'Environment default'],
      ['Idle Recovery', item.idle_recovery_enabled ? `${item.idle_recovery_delay_ms || 7000}ms` : 'Disabled'],
      ['Tags', item.tags?.join(', ') || 'None'],
      ['Tools', item.tool_names?.join(', ') || 'None'],
      ['Used By', item.associated_game_packs?.map((pack) => pack.name).join(', ') || 'No Game Packs'],
      ['Derived Quest Packs', item.compatible_quest_packs?.map((pack) => pack.name).join(', ') || 'None'],
      ['Rules', item.rules_prompt || 'No rules prompt'],
    ],
  },
  'quest-packs': {
    title: 'Quest Packs',
    endpoint: '/.netlify/functions/admin-get-quest-packs',
    responseKey: 'quest_packs',
    filters: ['query', 'tag', 'active'],
    listLabel: (item) => item.name,
    meta: (item) => `${item.question_count} questions • ${item.tags?.join(', ') || 'No tags'}`,
    detail: (item) => [
      ['Description', item.description || 'No description'],
      ['Category', item.category || 'Not available'],
      ['Tags', item.tags?.join(', ') || 'None'],
      ['Pack Image', item.pack_image || 'Not available'],
      ['Question Count', item.question_count],
      ['Compatible Game Modes', item.compatible_game_modes?.map((mode) => mode.name).join(', ') || 'None'],
      ['Associated Game Packs', item.associated_game_packs?.map((pack) => pack.name).join(', ') || 'None'],
      ['Legacy Pack Key', item.legacy_pack_key || 'Not available'],
    ],
  },
  questions: {
    title: 'Questions',
    endpoint: '/.netlify/functions/admin-get-questions',
    responseKey: 'questions',
    filters: ['query', 'source_required'],
    listLabel: (item) => item.question_text,
    meta: (item) => `${item.quest_pack?.name || item.pack_key || 'Unmapped'} • source ${item.has_source ? 'present' : 'missing'}`,
    detail: (item) => [
      ['Question', item.question_text],
      ['Quest Pack', item.quest_pack?.name || 'Not mapped'],
      ['Tags', item.tags?.join(', ') || 'Not available'],
      ['Answer', item.answer || 'Not available'],
      ['Source URL', item.source_url || 'Not available'],
      ['Status', item.status || 'Not available'],
      ['Created', item.created_at],
    ],
  },
  hosts: {
    title: 'Hosts',
    endpoint: '/.netlify/functions/admin-get-hosts',
    responseKey: 'hosts',
    filters: ['query', 'voice'],
    listLabel: (item) => item.name,
    meta: (item) => `${item.voice} • ${item.game_packs_using_host?.length || 0} game packs`,
    detail: (item) => [
      ['Description', item.description || 'No description'],
      ['Voice', item.voice],
      ['Temperature', item.temperature],
      ['Game Packs', item.game_packs_using_host?.map((pack) => pack.name).join(', ') || 'None'],
      ['Game Modes', item.associated_game_modes?.map((mode) => mode.name).join(', ') || 'None'],
      ['Updated', item.updated_at],
    ],
  },
};

const defaultFilters = {
  query: '',
  tag: '',
  active: '',
  include_questions: '',
  source_required: '',
  voice: '',
};

const defaultReferenceState = {
  loading: false,
  error: '',
  hosts: [],
  gameModes: [],
  questPacks: [],
};

const defaultSaveState = {
  saving: false,
  error: '',
  success: '',
};

const createGamePackEditorState = (item) => ({
  slug: item?.slug || '',
  name: item?.name || '',
  description: item?.description || '',
  host_id: item?.host_id || '',
  game_mode_id: item?.game_mode_id || '',
  image_filename: item?.image_filename || '',
  session_credit_cost: item ? String(item.session_credit_cost ?? 0) : '',
  admin_only: Boolean(item?.admin_only),
  active: item ? item.status === 'active' : true,
  available_environments: item?.available_environments?.length ? item.available_environments : DEFAULT_CONTENT_ENVIRONMENTS,
});

const createGameModeEditorState = (item) => ({
  slug: item?.slug || '',
  name: item?.name || '',
  description: item?.description || '',
  rules_prompt: item?.rules_prompt || '',
  include_questions: Boolean(item?.include_questions),
  tags: (item?.tags || []).join(', '),
  available_environments: item?.available_environments?.length ? item.available_environments : DEFAULT_CONTENT_ENVIRONMENTS,
  tool_names: item?.tool_names || [],
  play_style: item?.play_style || 'group',
  realtime_model: item?.realtime_model || '',
  idle_recovery_enabled: Boolean(item?.idle_recovery_enabled),
  idle_recovery_delay_ms: item ? String(item.idle_recovery_delay_ms ?? 7000) : '7000',
  idle_recovery_prompt: item?.idle_recovery_prompt || DEFAULT_IDLE_RECOVERY_PROMPT,
});

const createQuestPackEditorState = (item) => ({
  slug: item?.slug || '',
  name: item?.name || '',
  description: item?.description || '',
  pack_prompt: item?.pack_prompt || '',
  legacy_pack_key: item?.legacy_pack_key || '',
  pack_image: item?.pack_image || '',
  tags: (item?.tags || []).join(', '),
  active: item ? item.status === 'active' : true,
});

const parseCommaSeparatedList = (value) => (
  [...new Set(
    String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
  )]
);

const getHostImageSrc = (host) => {
  if (!host) {
    return '';
  }

  if (host.image_filename) {
    return `/images/hosts/${host.image_filename}`;
  }

  if (host.slug) {
    return `/images/hosts/${host.slug}.png`;
  }

  return '';
};

const AdminContentCollectionPage = ({ entityType }) => {
  const config = configs[entityType];
  const [filters, setFilters] = useState(defaultFilters);
  const [state, setState] = useState({
    loading: true,
    error: '',
    items: [],
  });
  const [selectedItemId, setSelectedItemId] = useState('');
  const [referenceState, setReferenceState] = useState(defaultReferenceState);
  const [editorState, setEditorState] = useState({});
  const [saveState, setSaveState] = useState(defaultSaveState);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();

    const loadItems = async () => {
      setState((current) => ({ ...current, loading: true, error: '' }));

      try {
        const payload = {
          ...(filters.query ? { query: filters.query } : {}),
          ...(filters.tag ? { tag: filters.tag } : {}),
          ...(filters.voice ? { voice: filters.voice } : {}),
          ...(filters.active === '' ? {} : { active: filters.active === 'true' }),
          ...(filters.include_questions === '' ? {} : { include_questions: filters.include_questions === 'true' }),
          ...(filters.source_required === '' ? {} : { source_required: filters.source_required === 'true' }),
        };

        const response = await fetch(config.endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load content.');
        }

        const items = data[config.responseKey] || [];
        setState({
          loading: false,
          error: '',
          items,
        });
        setSelectedItemId((current) => current && items.some((item) => String(item.id) === current) ? current : String(items[0]?.id || ''));
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }
        setState({
          loading: false,
          error: error.message || 'Unable to load content.',
          items: [],
        });
      }
    };

    loadItems();

    return () => controller.abort();
  }, [config.endpoint, config.responseKey, filters, entityType]);

  const selectedItem = useMemo(
    () => state.items.find((item) => String(item.id) === selectedItemId) || null,
    [selectedItemId, state.items],
  );

  useEffect(() => {
    if (entityType === 'game-packs') {
      setEditorState(createGamePackEditorState(selectedItem));
      setSaveState(defaultSaveState);
      return;
    }

    if (entityType === 'game-modes') {
      setEditorState(createGameModeEditorState(selectedItem));
      setSaveState(defaultSaveState);
      return;
    }

    if (entityType === 'quest-packs') {
      setEditorState(createQuestPackEditorState(selectedItem));
      setSaveState(defaultSaveState);
      return;
    }

    setEditorState({});
    setSaveState(defaultSaveState);
  }, [entityType, selectedItem]);

  useEffect(() => {
    if (entityType !== 'game-packs' && entityType !== 'game-modes') {
      setReferenceState(defaultReferenceState);
      return;
    }

    const token = localStorage.getItem('auth_token');
    const controller = new AbortController();

    const loadReferences = async () => {
      setReferenceState((current) => ({
        ...current,
        loading: true,
        error: '',
      }));

      try {
        const requests = entityType === 'game-packs'
          ? [
              fetch('/.netlify/functions/admin-get-hosts', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ page_size: 200 }),
                signal: controller.signal,
              }),
              fetch('/.netlify/functions/admin-get-game-modes', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ page_size: 200 }),
                signal: controller.signal,
              }),
            ]
          : [
              fetch('/.netlify/functions/admin-get-quest-packs', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ page_size: 200 }),
                signal: controller.signal,
              }),
            ];

        const responses = await Promise.all(requests);
        const data = await Promise.all(responses.map((response) => response.json().catch(() => ({}))));

        responses.forEach((response, index) => {
          if (!response.ok) {
            throw new Error(data[index]?.message || data[index]?.error || 'Unable to load editor options.');
          }
        });

        if (entityType === 'game-packs') {
          setReferenceState({
            loading: false,
            error: '',
            hosts: data[0]?.hosts || [],
            gameModes: data[1]?.game_modes || [],
            questPacks: [],
          });
          return;
        }

        setReferenceState({
          loading: false,
          error: '',
          hosts: [],
          gameModes: [],
          questPacks: data[0]?.quest_packs || [],
        });
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        setReferenceState({
          loading: false,
          error: error.message || 'Unable to load editor options.',
          hosts: [],
          gameModes: [],
          questPacks: [],
        });
      }
    };

    loadReferences();

    return () => controller.abort();
  }, [entityType]);

  const hostImageSrc = entityType === 'hosts' ? getHostImageSrc(selectedItem) : '';
  const selectedGameModeOption = entityType === 'game-packs'
    ? referenceState.gameModes.find((mode) => mode.id === editorState.game_mode_id) || null
    : null;
  const derivedQuestPacks = entityType === 'game-modes'
    ? referenceState.questPacks.filter((questPack) => {
        const modeTags = parseCommaSeparatedList(editorState.tags).map((tag) => tag.toLowerCase());
        return modeTags.length > 0 && (questPack.tags || []).some((tag) => modeTags.includes(tag));
      })
    : [];

  const handleFieldChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;

    setEditorState((current) => ({
      ...current,
      [field]: value,
    }));
    setSaveState(defaultSaveState);
  };

  const handleToolSelectionChange = (event) => {
    const selectedToolNames = Array.from(event.target.selectedOptions, (option) => option.value);

    setEditorState((current) => ({
      ...current,
      tool_names: selectedToolNames,
    }));
    setSaveState(defaultSaveState);
  };

  const handleEnvironmentSelectionChange = (environment) => (event) => {
    const checked = event.target.checked;

    setEditorState((current) => {
      const currentEnvironments = current.available_environments?.length
        ? current.available_environments
        : DEFAULT_CONTENT_ENVIRONMENTS;
      const nextEnvironments = checked
        ? [...new Set([...currentEnvironments, environment])]
        : currentEnvironments.filter((item) => item !== environment);

      return {
        ...current,
        available_environments: nextEnvironments.length ? nextEnvironments : currentEnvironments,
      };
    });
    setSaveState(defaultSaveState);
  };

  const handleSaveSelectedItem = async () => {
    if ((entityType !== 'game-packs' && entityType !== 'game-modes' && entityType !== 'quest-packs') || !selectedItem) {
      return;
    }

    const token = localStorage.getItem('auth_token');

    setSaveState({
      saving: true,
      error: '',
      success: '',
    });

    try {
      let endpoint = '';
      let payload = null;

      if (entityType === 'game-packs') {
        const parsedCost = Number(editorState.session_credit_cost);

        if (!Number.isInteger(parsedCost) || parsedCost < 0) {
          throw new Error('Cost must be a non-negative whole number of credits.');
        }

        payload = {
          slug: editorState.slug.trim(),
          name: editorState.name.trim(),
          description: editorState.description.trim(),
          host_id: editorState.host_id,
          game_mode_id: editorState.game_mode_id,
          image_filename: editorState.image_filename.trim(),
          session_credit_cost: parsedCost,
          admin_only: Boolean(editorState.admin_only),
          active: Boolean(editorState.active),
          available_environments: editorState.available_environments?.length
            ? editorState.available_environments
            : DEFAULT_CONTENT_ENVIRONMENTS,
        };
        endpoint = '/.netlify/functions/updateGamePack';
      } else if (entityType === 'game-modes') {
        payload = {
          slug: editorState.slug.trim(),
          name: editorState.name.trim(),
          description: editorState.description.trim(),
          rules_prompt: editorState.rules_prompt.trim(),
          include_questions: Boolean(editorState.include_questions),
          tool_names: editorState.tool_names || [],
          tags: parseCommaSeparatedList(editorState.tags),
          available_environments: editorState.available_environments?.length
            ? editorState.available_environments
            : DEFAULT_CONTENT_ENVIRONMENTS,
          play_style: editorState.play_style || 'group',
          realtime_model: editorState.realtime_model.trim(),
          idle_recovery_enabled: Boolean(editorState.idle_recovery_enabled),
          idle_recovery_delay_ms: Number.parseInt(editorState.idle_recovery_delay_ms, 10) || 7000,
          idle_recovery_prompt: editorState.idle_recovery_prompt.trim(),
        };
        endpoint = '/.netlify/functions/updateGameMode';
      } else {
        payload = {
          slug: editorState.slug.trim(),
          name: editorState.name.trim(),
          description: editorState.description.trim(),
          pack_prompt: editorState.pack_prompt.trim(),
          legacy_pack_key: editorState.legacy_pack_key.trim(),
          pack_image: editorState.pack_image.trim(),
          tags: parseCommaSeparatedList(editorState.tags),
          play_style: editorState.play_style || 'group',
          active: Boolean(editorState.active),
        };
        endpoint = '/.netlify/functions/updateQuestPack';
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          id: selectedItem.id,
          payload,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(
          data?.error || `Unable to save ${
            entityType === 'game-packs' ? 'game pack' : entityType === 'game-modes' ? 'game mode' : 'quest pack'
          } settings.`,
        );
      }

      if (entityType === 'game-packs') {
        const nextHost = referenceState.hosts.find((host) => host.id === payload.host_id) || null;
        const nextGameMode = referenceState.gameModes.find((mode) => mode.id === payload.game_mode_id) || null;

        setState((current) => ({
          ...current,
          items: current.items.map((item) => (
            item.id === selectedItem.id
              ? {
                  ...item,
                  slug: payload.slug,
                  name: payload.name,
                  description: payload.description,
                  host_id: payload.host_id,
                  game_mode_id: payload.game_mode_id,
                  image_filename: payload.image_filename || null,
                  session_credit_cost: payload.session_credit_cost,
                  admin_only: payload.admin_only,
                  status: payload.active ? 'active' : 'inactive',
                  available_environments: payload.available_environments,
                  associated_host: nextHost ? { id: nextHost.id, slug: nextHost.slug, name: nextHost.name } : null,
                  game_mode: nextGameMode ? {
                    id: nextGameMode.id,
                    slug: nextGameMode.slug,
                    name: nextGameMode.name,
                    tags: nextGameMode.tags || [],
                    include_questions: Boolean(nextGameMode.include_questions),
                    available_environments: nextGameMode.available_environments || DEFAULT_CONTENT_ENVIRONMENTS,
                  } : null,
                  updated_at: data?.game_pack?.updated_at || item.updated_at,
                }
              : item
          )),
        }));
      } else if (entityType === 'game-modes') {
        setState((current) => ({
          ...current,
          items: current.items.map((item) => (
            item.id === selectedItem.id
              ? {
                  ...item,
                  slug: payload.slug,
                  name: payload.name,
                  description: payload.description,
                  rules_prompt: payload.rules_prompt,
                  include_questions: payload.include_questions,
                  tags: payload.tags,
                  available_environments: payload.available_environments,
                  tool_names: payload.tool_names,
                  play_style: payload.play_style,
                  realtime_model: payload.realtime_model,
                  idle_recovery_enabled: payload.idle_recovery_enabled,
                  idle_recovery_delay_ms: payload.idle_recovery_delay_ms,
                  idle_recovery_prompt: payload.idle_recovery_prompt,
                  compatible_quest_packs: derivedQuestPacks.map((questPack) => ({
                    id: questPack.id,
                    name: questPack.name,
                  })),
                  updated_at: data?.game_mode?.updated_at || item.updated_at,
                }
              : item
          )),
        }));
      } else {
        setState((current) => ({
          ...current,
          items: current.items.map((item) => (
            item.id === selectedItem.id
              ? {
                  ...item,
                  slug: payload.slug,
                  name: payload.name,
                  description: payload.description,
                  pack_prompt: payload.pack_prompt,
                  legacy_pack_key: payload.legacy_pack_key || null,
                  pack_image: payload.pack_image || null,
                  tags: payload.tags,
                  play_style: payload.play_style,
                  status: payload.active ? 'active' : 'inactive',
                  updated_at: data?.quest_pack?.updated_at || item.updated_at,
                }
              : item
          )),
        }));
      }

      setSaveState({
        saving: false,
        error: '',
        success: `${
          entityType === 'game-packs' ? 'Game pack' : entityType === 'game-modes' ? 'Game mode' : 'Quest pack'
        } updated.`,
      });
    } catch (error) {
      setSaveState({
        saving: false,
        error: error.message || `Unable to save ${
          entityType === 'game-packs' ? 'game pack' : entityType === 'game-modes' ? 'game mode' : 'quest pack'
        } settings.`,
        success: '',
      });
    }
  };

  return (
    <section className="cq-admin-page">
      <header className="cq-admin-page__header">
        <div>
          <p className="cq-admin-page__eyebrow">Current Content Display</p>
          <h2 className="cq-admin-page__title">{config.title}</h2>
          <p className="cq-admin-page__copy">
            {entityType === 'game-packs'
              ? 'Review relationships and update the live game pack fields used by the playable menu.'
              : entityType === 'game-modes'
                ? 'Inspect relationships and edit the live game mode rules, tags, and tool access.'
                : entityType === 'quest-packs'
                  ? 'Inspect relationships and edit the live quest pack fields used by the selection flow.'
                  : 'Read-only inspection with relationship visibility. No editing is enabled here.'}
          </p>
        </div>
      </header>

      <nav className="cq-admin-content-tabs" aria-label="Content sections">
        {tabs.map((tab) => (
          <NavLink key={tab.key} to={tab.to} className={({ isActive }) => `cq-admin-content-tab${isActive ? ' is-active' : ''}`}>
            {tab.label}
          </NavLink>
        ))}
      </nav>

      <div className="cq-admin-panel cq-admin-content-filters">
        {config.filters.includes('query') ? (
          <label className="cq-admin-field">
            <span>Search</span>
            <input className="cq-admin-input" value={filters.query} onChange={(event) => setFilters((current) => ({ ...current, query: event.target.value }))} />
          </label>
        ) : null}
        {config.filters.includes('tag') ? (
          <label className="cq-admin-field">
            <span>Tag</span>
            <input className="cq-admin-input" value={filters.tag} onChange={(event) => setFilters((current) => ({ ...current, tag: event.target.value }))} />
          </label>
        ) : null}
        {config.filters.includes('voice') ? (
          <label className="cq-admin-field">
            <span>Voice</span>
            <input className="cq-admin-input" value={filters.voice} onChange={(event) => setFilters((current) => ({ ...current, voice: event.target.value }))} />
          </label>
        ) : null}
        {config.filters.includes('active') ? (
          <label className="cq-admin-field">
            <span>Status</span>
            <select className="cq-admin-input" value={filters.active} onChange={(event) => setFilters((current) => ({ ...current, active: event.target.value }))}>
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </label>
        ) : null}
        {config.filters.includes('include_questions') ? (
          <label className="cq-admin-field">
            <span>Requires Quest Pack</span>
            <select className="cq-admin-input" value={filters.include_questions} onChange={(event) => setFilters((current) => ({ ...current, include_questions: event.target.value }))}>
              <option value="">All</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </label>
        ) : null}
        {config.filters.includes('source_required') ? (
          <label className="cq-admin-field">
            <span>Source</span>
            <select className="cq-admin-input" value={filters.source_required} onChange={(event) => setFilters((current) => ({ ...current, source_required: event.target.value }))}>
              <option value="">All</option>
              <option value="true">Source Present</option>
              <option value="false">Source Missing</option>
            </select>
          </label>
        ) : null}
      </div>

      {state.loading ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">Loading content...</p></div> : null}
      {!state.loading && state.error ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">{state.error}</p></div> : null}
      {!state.loading && !state.error && !state.items.length ? <div className="cq-admin-empty-state"><p className="cq-admin-empty-state__title">No content found.</p></div> : null}

      {!state.loading && !state.error && state.items.length ? (
        <div className="cq-admin-content-detail-grid">
          <section className="cq-admin-panel">
            <ul className="cq-admin-content-list">
              {state.items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className={`cq-admin-content-list__item${String(item.id) === selectedItemId ? ' is-active' : ''}`}
                    onClick={() => setSelectedItemId(String(item.id))}
                  >
                    <span className="cq-admin-content-list__title">{config.listLabel(item)}</span>
                    <span className="cq-admin-content-list__meta">{config.meta(item)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="cq-admin-panel">
            {selectedItem ? (
              <>
                <h3 className="cq-admin-section-title">{config.listLabel(selectedItem)}</h3>
                {entityType === 'hosts' && hostImageSrc ? (
                  <div className="cq-admin-content-host-image">
                    <img
                      className="cq-admin-content-host-image__img"
                      src={hostImageSrc}
                      alt={`${selectedItem.name} host portrait`}
                    />
                  </div>
                ) : null}
                <dl className="cq-admin-definition-list">
                  {config.detail(selectedItem).map(([label, value]) => (
                    <div key={label}>
                      <dt>{label}</dt>
                      <dd>{value || 'Not available'}</dd>
                    </div>
                  ))}
                </dl>
                {entityType === 'game-packs' ? (
                  <div className="cq-admin-content-editor">
                    <h4 className="cq-admin-section-title cq-admin-content-editor__title">Game Pack Settings</h4>
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-field">
                        <span>Slug</span>
                        <input className="cq-admin-input" value={editorState.slug || ''} readOnly aria-readonly="true" />
                      </label>
                      <label className="cq-admin-field">
                        <span>Name</span>
                        <input className="cq-admin-input" value={editorState.name || ''} onChange={handleFieldChange('name')} />
                      </label>
                    </div>
                    <label className="cq-admin-field">
                      <span>Description</span>
                      <textarea className="cq-admin-input cq-admin-textarea" value={editorState.description || ''} onChange={handleFieldChange('description')} />
                    </label>
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-field">
                        <span>Host</span>
                        <select className="cq-admin-input" value={editorState.host_id || ''} onChange={handleFieldChange('host_id')}>
                          <option value="">Select host</option>
                          {referenceState.hosts.map((host) => (
                            <option key={host.id} value={host.id}>{host.name}</option>
                          ))}
                        </select>
                      </label>
                      <label className="cq-admin-field">
                        <span>Game Mode</span>
                        <select className="cq-admin-input" value={editorState.game_mode_id || ''} onChange={handleFieldChange('game_mode_id')}>
                          <option value="">Select game mode</option>
                          {referenceState.gameModes.map((mode) => (
                            <option key={mode.id} value={mode.id}>{mode.name}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                    {selectedGameModeOption ? (
                      <p className="cq-admin-content-editor__hint">
                        Mode tags: {selectedGameModeOption.tags?.join(', ') || 'None'}.
                        {selectedGameModeOption.include_questions ? ' This mode requires a quest pack.' : ' This mode does not require a quest pack.'}
                      </p>
                    ) : null}
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-field">
                        <span>Credits</span>
                        <input
                          className="cq-admin-input"
                          type="number"
                          min="0"
                          step="1"
                          value={editorState.session_credit_cost || ''}
                          onChange={handleFieldChange('session_credit_cost')}
                        />
                      </label>
                      <label className="cq-admin-field">
                        <span>Game Image Filename</span>
                        <input
                          className="cq-admin-input"
                          placeholder="classic-cringe-card.png"
                          value={editorState.image_filename || ''}
                          onChange={handleFieldChange('image_filename')}
                        />
                      </label>
                    </div>
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-content-editor__checkbox">
                        <input type="checkbox" checked={Boolean(editorState.admin_only)} onChange={handleFieldChange('admin_only')} />
                        <span>Admin only</span>
                      </label>
                      <label className="cq-admin-content-editor__checkbox">
                        <input type="checkbox" checked={Boolean(editorState.active)} onChange={handleFieldChange('active')} />
                        <span>Active</span>
                      </label>
                    </div>
                    <div className="cq-admin-content-editor__grid">
                      {CONTENT_ENVIRONMENT_OPTIONS.map((option) => (
                        <label key={option.value} className="cq-admin-content-editor__checkbox">
                          <input
                            type="checkbox"
                            checked={(editorState.available_environments || DEFAULT_CONTENT_ENVIRONMENTS).includes(option.value)}
                            onChange={handleEnvironmentSelectionChange(option.value)}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {referenceState.loading ? <p className="cq-admin-content-editor__hint">Loading editor options...</p> : null}
                    {referenceState.error ? <p className="cq-admin-content-editor__error">{referenceState.error}</p> : null}
                    <button
                      type="button"
                      className="cq-admin-action-button"
                      disabled={saveState.saving || referenceState.loading}
                      onClick={handleSaveSelectedItem}
                    >
                      {saveState.saving ? 'Saving...' : 'Save Game Pack'}
                    </button>
                    {saveState.error ? <p className="cq-admin-content-editor__error">{saveState.error}</p> : null}
                    {saveState.success ? <p className="cq-admin-content-editor__success">{saveState.success}</p> : null}
                  </div>
                ) : null}
                {entityType === 'game-modes' ? (
                  <div className="cq-admin-content-editor">
                    <h4 className="cq-admin-section-title cq-admin-content-editor__title">Game Mode Settings</h4>
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-field">
                        <span>Slug</span>
                        <input className="cq-admin-input" value={editorState.slug || ''} readOnly aria-readonly="true" />
                      </label>
                      <label className="cq-admin-field">
                        <span>Name</span>
                        <input className="cq-admin-input" value={editorState.name || ''} onChange={handleFieldChange('name')} />
                      </label>
                    </div>
                    <label className="cq-admin-field">
                      <span>Description</span>
                      <textarea className="cq-admin-input cq-admin-textarea" value={editorState.description || ''} onChange={handleFieldChange('description')} />
                    </label>
                    <label className="cq-admin-field">
                      <span>Rules Prompt</span>
                      <textarea className="cq-admin-input cq-admin-textarea cq-admin-content-editor__rules" value={editorState.rules_prompt || ''} onChange={handleFieldChange('rules_prompt')} />
                    </label>
                    <label className="cq-admin-field">
                      <span>Tags</span>
                      <input className="cq-admin-input" placeholder="party, cringe, icebreaker" value={editorState.tags || ''} onChange={handleFieldChange('tags')} />
                    </label>
                    <label className="cq-admin-field">
                      <span>Play Style</span>
                      <select className="cq-admin-input" value={editorState.play_style || 'group'} onChange={handleFieldChange('play_style')}>
                        <option value="group">Group</option>
                        <option value="solo">Solo</option>
                        <option value="group_solo">Group + Solo</option>
                      </select>
                    </label>
                    <div className="cq-admin-content-editor__grid">
                      {CONTENT_ENVIRONMENT_OPTIONS.map((option) => (
                        <label key={option.value} className="cq-admin-content-editor__checkbox">
                          <input
                            type="checkbox"
                            checked={(editorState.available_environments || DEFAULT_CONTENT_ENVIRONMENTS).includes(option.value)}
                            onChange={handleEnvironmentSelectionChange(option.value)}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                    <label className="cq-admin-field">
                      <span>Realtime Model</span>
                      <input className="cq-admin-input" placeholder="Default: OPENAI_REALTIME_MODEL" value={editorState.realtime_model || ''} onChange={handleFieldChange('realtime_model')} />
                    </label>
                    <label className="cq-admin-content-editor__checkbox">
                      <input type="checkbox" checked={Boolean(editorState.idle_recovery_enabled)} onChange={handleFieldChange('idle_recovery_enabled')} />
                      <span>Enable idle recovery</span>
                    </label>
                    <label className="cq-admin-field">
                      <span>Idle Recovery Delay (ms)</span>
                      <input className="cq-admin-input" type="number" min="1" step="100" value={editorState.idle_recovery_delay_ms || '7000'} onChange={handleFieldChange('idle_recovery_delay_ms')} />
                    </label>
                    <label className="cq-admin-field cq-admin-field--full">
                      <span>Idle Recovery Prompt</span>
                      <textarea className="cq-admin-input cq-admin-textarea" value={editorState.idle_recovery_prompt || ''} onChange={handleFieldChange('idle_recovery_prompt')} />
                    </label>
                    <label className="cq-admin-content-editor__checkbox">
                      <input type="checkbox" checked={Boolean(editorState.include_questions)} onChange={handleFieldChange('include_questions')} />
                      <span>Requires quest pack questions</span>
                    </label>
                    <label className="cq-admin-field cq-admin-field--full">
                      <span>Tool Access</span>
                      <select
                        className="cq-admin-input cq-admin-input--multiselect"
                        multiple
                        value={editorState.tool_names || []}
                        onChange={handleToolSelectionChange}
                      >
                        {GAME_MODE_TOOL_OPTIONS.map((toolName) => (
                          <option key={toolName} value={toolName}>{toolName}</option>
                        ))}
                      </select>
                    </label>
                    <p className="cq-admin-content-editor__hint">Hold Command on macOS or Ctrl on Windows to select multiple tools.</p>
                    {derivedQuestPacks.length ? (
                      <p className="cq-admin-content-editor__hint">
                        Matching quest packs: {derivedQuestPacks.map((questPack) => questPack.name).join(', ')}
                      </p>
                    ) : (
                      <p className="cq-admin-content-editor__hint">No quest packs currently match these tags.</p>
                    )}
                    {referenceState.loading ? <p className="cq-admin-content-editor__hint">Loading derived quest pack matches...</p> : null}
                    {referenceState.error ? <p className="cq-admin-content-editor__error">{referenceState.error}</p> : null}
                    <button
                      type="button"
                      className="cq-admin-action-button"
                      disabled={saveState.saving || referenceState.loading}
                      onClick={handleSaveSelectedItem}
                    >
                      {saveState.saving ? 'Saving...' : 'Save Game Mode'}
                    </button>
                    {saveState.error ? <p className="cq-admin-content-editor__error">{saveState.error}</p> : null}
                    {saveState.success ? <p className="cq-admin-content-editor__success">{saveState.success}</p> : null}
                  </div>
                ) : null}
                {entityType === 'quest-packs' ? (
                  <div className="cq-admin-content-editor">
                    <h4 className="cq-admin-section-title cq-admin-content-editor__title">Quest Pack Settings</h4>
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-field">
                        <span>Slug</span>
                        <input className="cq-admin-input" value={editorState.slug || ''} readOnly aria-readonly="true" />
                      </label>
                      <label className="cq-admin-field">
                        <span>Name</span>
                        <input className="cq-admin-input" value={editorState.name || ''} onChange={handleFieldChange('name')} />
                      </label>
                    </div>
                    <label className="cq-admin-field">
                      <span>Description</span>
                      <textarea className="cq-admin-input cq-admin-textarea" value={editorState.description || ''} onChange={handleFieldChange('description')} />
                    </label>
                    <label className="cq-admin-field">
                      <span>Pack Prompt</span>
                      <textarea className="cq-admin-input cq-admin-textarea cq-admin-content-editor__rules" value={editorState.pack_prompt || ''} onChange={handleFieldChange('pack_prompt')} />
                    </label>
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-field">
                        <span>Pack Image</span>
                        <input className="cq-admin-input" value={editorState.pack_image || ''} onChange={handleFieldChange('pack_image')} />
                      </label>
                      <label className="cq-admin-field">
                        <span>Legacy Pack Key</span>
                        <input className="cq-admin-input" value={editorState.legacy_pack_key || ''} onChange={handleFieldChange('legacy_pack_key')} />
                      </label>
                    </div>
                    <p className="cq-admin-content-editor__hint">Enter the image filename only. Quest pack art is served from `/images/quest-packs/`.</p>
                    <div className="cq-admin-content-editor__grid">
                      <label className="cq-admin-field">
                        <span>Tags</span>
                        <input className="cq-admin-input" value={editorState.tags || ''} onChange={handleFieldChange('tags')} />
                      </label>
                    </div>
                    <label className="cq-admin-content-editor__checkbox">
                      <input type="checkbox" checked={Boolean(editorState.active)} onChange={handleFieldChange('active')} />
                      <span>Active</span>
                    </label>
                    <button
                      type="button"
                      className="cq-admin-action-button"
                      disabled={saveState.saving}
                      onClick={handleSaveSelectedItem}
                    >
                      {saveState.saving ? 'Saving...' : 'Save Quest Pack'}
                    </button>
                    {saveState.error ? <p className="cq-admin-content-editor__error">{saveState.error}</p> : null}
                    {saveState.success ? <p className="cq-admin-content-editor__success">{saveState.success}</p> : null}
                  </div>
                ) : null}
              </>
            ) : (
              <div className="cq-admin-empty-inline">Select an item to inspect its relationships.</div>
            )}
          </section>
        </div>
      ) : null}
    </section>
  );
};

export default AdminContentCollectionPage;
